/**
 * SmoothTransition - A utility function for smooth marker transitions on maps
 *
 * This utility provides smooth animation transitions for map markers, using
 * requestAnimationFrame for optimal performance and easeInOutCubic easing
 * for natural movement.
 *
 * @param start - Starting position with x, y coordinates
 * @param end - Ending position with x, y coordinates
 * @param setPosition - Callback function to update position during animation
 * @returns cleanup function to cancel animation if needed
 */

const easeInOutCubic = (t: number): number => {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2
}

export const smoothTransition = (start: any, end: any, setPosition: any) => {
  if (!start?.x || !start?.y || !end?.x || !end?.y) {
    console.warn('Invalid position data for transition')
    return
  }

  const duration = 6000
  const startTime = performance.now()
  let animationFrameId: number | undefined
  let cleanup: (() => void) | undefined
  let lastProgress = 0

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const rawProgress = Math.min(elapsed / duration, 1)
    // Smooth out the progress to prevent jerky movements
    const progress = Math.max(lastProgress, easeInOutCubic(rawProgress))
    lastProgress = progress

    const newLng = start.x + (end.x - start.x) * progress
    const newLat = start.y + (end.y - start.y) * progress

    setPosition({lat: newLat, lng: newLng})

    if (rawProgress < 1) {
      animationFrameId = requestAnimationFrame(animate)
    } else {
      // Ensure final position is set
      setPosition({lat: end.y, lng: end.x})
      cleanup?.()
    }
  }

  cleanup = () => {
    if (animationFrameId !== undefined) {
      cancelAnimationFrame(animationFrameId)
    }
  }

  animationFrameId = requestAnimationFrame(animate)
  return cleanup
}